import { usePaymentApiV1 } from '../../../clients/quipli-service/payment/payment-api-v1';
import { useCallback, useEffect, useState } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { CustomerOrder } from '../../../clients/customer-api/order/customer-order';
import { debounce } from 'lodash';
import { toast } from '../../../util/toasts';
import { CustomerPayment } from '../../../clients/customer-api/invoice/customer-invoice';

export default function StripePaymentAuthorization({
  orderId,
  payment,
}: {
  orderId: number;
  payment?: CustomerPayment;
}) {
  const { getStripeSettings } = usePaymentApiV1();
  const { getOrderById, refreshPayments } = useCustomerAPI();

  const [stripe, setStripe] = useState<Stripe>();
  const [order, setOrder] = useState<CustomerOrder>();

  useEffect(() => {
    getStripeSettings().then((settings) => {
      loadStripe(settings.publishableKey, {
        stripeAccount: settings.accountType === 'standard' ? settings.connectedAccountId : undefined,
      }).then((stripe) => {
        if (stripe) {
          setStripe(stripe);
        } else {
          throw new Error('Unable to retrieve stripe instance');
        }
      });
    });
  }, []);

  useEffect(() => {
    getOrderById(orderId).then((customerOrder) => {
      setOrder(customerOrder);
    });
  }, []);

  async function authenticatePayments() {
    if (stripe && order) {
      try {
        await Promise.all(
          order.invoices.map(async (invoice) => {
            if (payment && payment.clientSecret) {
              await stripe.handleNextAction({ clientSecret: payment.clientSecret });
            } else {
              for (const authPayment of invoice.payments.filter((payment) => {
                return payment.gatewayStatus === 'requires_action' && payment.clientSecret;
              })) {
                if (authPayment.clientSecret) {
                  await stripe.confirmCardPayment(authPayment.clientSecret, {
                    payment_method: authPayment.paymentMethod,
                  });
                }
              }
            }
          }),
        );
      } catch (e: any) {
        toast('Unable to capture authentication', 'error');
      }
      try {
        const payments = await refreshPayments(orderId);
        if (
          (payment && payment.gatewayStatus !== 'requires_action') ||
          !payments.some((p) => {
            return p.gatewayStatus === 'requires_action';
          })
        ) {
          const path = window.location.href.split('/order')[0];
          window.location.href = `${path}/account/view_orders/${orderId}`;
        }
      } catch (e: any) {
        toast('An unexpected error occurred');
      }
    }
  }

  const authenticate = useCallback(
    debounce(function () {
      return authenticatePayments();
    }, 300),
    [order],
  );

  useEffect(() => {
    authenticate();
  }, [stripe, order]);

  return <></>;
}
